import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PetResponse } from 'we-tree-api-client/v2';
import { PET_CATEGORY_VALUES } from '@/types/pet-category';
import { mediaApi } from '@/api/mediaWrapper';
import { duration } from '@/utils/duration';
import { calendarYearFromStoredDate, localCalendarDateFromStored } from '@/utils/dateFormatting';
import PawPrint from '@/icons/PawPrint';
import UserIcon from '@/icons/User';
import '@/components/person/person-card.css';
import '@/styles/members-cards.css';

interface PetCardProps {
  pet: PetResponse;
  familyId?: string;
  onProfileClick?: (familyMemberId: string) => void;
}

function formatPetKind(category: string, subType?: string | null): string {
  const label = category.charAt(0).toUpperCase() + category.slice(1);
  if (subType?.trim()) {
    return `${label} · ${subType.trim()}`;
  }
  return label;
}

/** Members-list / profile / tree pet card — always uses shared members-cards styling. */
export const PetCard: React.FC<PetCardProps> = ({ pet, familyId, onProfileClick }) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!familyId) return;
    let cancelled = false;
    mediaApi.getPrimaryProfilePhotoDisplay(familyId, pet.familyMemberId, '40x40').then((result) => {
      if (!cancelled && result.success && result.data?.url && result.data?.displayStatus === 'safe') {
        setPhotoUrl(result.data.url);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [familyId, pet.familyMemberId]);

  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onProfileClick?.(pet.familyMemberId);
  };

  const birthOrAdoptionDate = pet.birthDate ?? pet.adoptionDate;
  const birthYear = calendarYearFromStoredDate(birthOrAdoptionDate);
  const deathYear = calendarYearFromStoredDate(pet.deathDate);
  const isDeceased = pet.livingStatus === 'deceased' && pet.deathDate;

  let age: string | null = null;
  if (birthOrAdoptionDate && !(pet.livingStatus === 'deceased' && !pet.deathDate)) {
    const birth = localCalendarDateFromStored(birthOrAdoptionDate);
    const endDateResolved = pet.deathDate ? localCalendarDateFromStored(pet.deathDate) : new Date();
    if (birth && endDateResolved) {
      const d = duration(birth, endDateResolved);
      age = d ? tCommon('duration.ageOld', { duration: d }) : null;
    }
  }

  const category = pet.category ?? 'other';
  const categoryKey = PET_CATEGORY_VALUES.includes(category) ? category : 'other';
  const categoryClass = `person-card--pet-category-${categoryKey}`;

  const cardClasses = ['person-card', 'person-card--pet', 'members-list-pet', categoryClass];
  if (pet.livingStatus === 'deceased') {
    cardClasses.push('person-card--deceased');
  }

  const yearsLabel =
    birthYear == null
      ? null
      : isDeceased && deathYear
        ? tCommon('genealogy.bornYearRange', { birth: birthYear, death: deathYear })
        : tCommon('genealogy.bornYear', { year: birthYear });

  return (
    <div className="pet-card-host members-cards-scope">
      <div className={cardClasses.join(' ')} data-family-member-id={pet.familyMemberId} data-category={category}>
        <div className="person-card-name-container">
          <div className="person-card-name">{pet.displayName}</div>
        </div>
        <div className="person-card-body">
          <div className="person-card-photo">
            {photoUrl ? (
              <img src={photoUrl} alt="" className="person-card-photo-img" />
            ) : (
              <div className="person-card-photo-placeholder">
                <PawPrint width={22} height={22} />
              </div>
            )}
          </div>
          <div className="person-card-metadata">
            <div className="person-card-kind">{formatPetKind(category, pet.subType)}</div>
            <div className="person-card-years">{yearsLabel}</div>
            <div className="person-card-age">{age !== null ? `(${age})` : null}</div>
          </div>
        </div>
        {onProfileClick && (
          <div className="person-card-bottom-row">
            <div className="person-card-actions">
              <button
                type="button"
                className="person-card-action person-card-action--profile"
                onClick={handleProfileClick}
                aria-label={t('personCard.openProfile')}
                title={t('personCard.openProfile')}
              >
                <UserIcon width={14} height={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PetCard;
